import React, { useState } from "react"
import { useMutation } from "@apollo/client"
import { useIntl, FormattedMessage } from "gatsby-plugin-intl"
import { CustomModal, Button } from "@tmu/components/common"
import TrustAIStudio from "./TrustAIStudio"
import TrustAIPreview from "./TrustAIPreview"
import TrustAILoading from "./TrustAILoading"
import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationTriangle, faTimes } from "@fortawesome/pro-regular-svg-icons"
import { GENERATE_CAMPAIGN_DESCRIPTION, TRANSLATE_CAMPAIGN_DESCRIPTION } from "@tmu/apollo/dashboard/mutations/trustai"

const ErrorStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 500px;
  max-width: 100%;
`

const HeaderSection = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Branding = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LogoSphere = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #00d2ff 0%, #3a7bd5 100%);
  box-shadow: 0 4px 10px rgba(58, 123, 213, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    left: 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%);
  }
`

const Title = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 250px;
`

const ErrorIcon = styled.div`
  color: #ef4444;
  font-size: 7rem;
  margin-bottom: 24px;
  line-height: 1;
  
  svg {
    width: 1em;
    height: 1em;
  }
`

const ErrorTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`

const ErrorMessage = styled.p`
  font-size: 0.95rem;
  color: #4b5563;
  margin-bottom: 24px;
`

const TrustAIModal = ({ isOpen, onClose, onInsert, category }) => {
    const { formatMessage, locale } = useIntl()
    const [step, setStep] = useState('studio') // 'studio' | 'generating' | 'preview' | 'translating' | 'error'
    const [generatedTitle, setGeneratedTitle] = useState("")
    const [generatedContent, setGeneratedContent] = useState("")
    const [error, setError] = useState(null)
    const [lastWizardData, setLastWizardData] = useState(null)

    const [generateMutation] = useMutation(GENERATE_CAMPAIGN_DESCRIPTION)
    const [translateMutation] = useMutation(TRANSLATE_CAMPAIGN_DESCRIPTION)

    const handleGenerate = async (wizardData) => {
        setLastWizardData(wizardData)
        setStep('generating')
        setError(null)
        try {
            console.log("TrustAI: Starting generation with", wizardData)
            const { data } = await generateMutation({
                variables: {
                    goal: wizardData.goal,
                    story: wizardData.story,
                    amountUsage: wizardData.amountUsage,
                    beneficiary: wizardData.beneficiary,
                    trustElements: wizardData.trustElements,
                    tone: wizardData.tone,
                    campaignCategory: category,
                    language: locale
                }
            })
            console.log("TrustAI Generate Response Data:", data)
            if (data?.generateCampaignDescription) {
                const titleText = data.generateCampaignDescription.title || ""
                const contentText = data.generateCampaignDescription.content || ""

                // Double check if the content itself indicates an error (e.g. from a backend fallback)
                if (titleText.startsWith("Error") || titleText.startsWith("Fail")) {
                    console.error("TrustAI Generate Response Content Error:", titleText)
                    setError(titleText)
                    setStep('error')
                    return
                }

                setGeneratedTitle(titleText)
                setGeneratedContent(contentText)
                setStep('preview')
            } else {
                console.error("TrustAI Generate Response missing generateCampaignDescription object")
                setError(formatMessage({ id: "trustai::error.no_content", defaultMessage: "AI could not generate content. Please try again with more details." }))
                setStep('error')
            }
        } catch (e) {
            console.error("TrustAI Generation Failed", e)
            const msg = e.message || "An unexpected error occurred while generating content."
            setError(msg)
            setStep('error')
        }
    }

    const handleRegenerate = async (newTone) => {
        if (!lastWizardData) return
        const newData = { ...lastWizardData, tone: newTone }
        await handleGenerate(newData)
    }

    const handleApprove = async (finalTitle, finalContent, mode = 'all') => {
        if (mode === 'single') {
            console.log("TrustAI Approve single mode - Inserting defaults:", { [locale]: finalTitle })
            onInsert({
                title: { [locale]: finalTitle },
                description: { [locale]: finalContent }
            })
            handleClose()
            return
        }

        setStep('translating')
        setError(null)
        try {
            // Step 1: Translate title
            console.log("TrustAI: Step 1 - Translating title...")
            const titleRes = await translateMutation({
                variables: {
                    content: finalTitle,
                    sourceLanguage: locale
                }
            })
            const titleRawJson = titleRes?.data?.translateCampaignDescription?.translations
            console.log("TrustAI: Step 1 complete - Title raw JSON:", titleRawJson)

            // Step 2: Translate description
            console.log("TrustAI: Step 2 - Translating description...")
            const descRes = await translateMutation({
                variables: {
                    content: finalContent,
                    sourceLanguage: locale
                }
            })
            const descRawJson = descRes?.data?.translateCampaignDescription?.translations
            console.log("TrustAI: Step 2 complete - Desc raw JSON:", descRawJson)

            // Helper: parse potentially double-serialized JSON from API
            const safeParse = (raw) => {
                let parsed = JSON.parse(raw)
                // API returns double-serialized JSON: a string inside a string
                // First parse unwraps the outer quotes, second parse gets the object
                if (typeof parsed === 'string') {
                    parsed = JSON.parse(parsed)
                }
                return parsed
            }

            // Step 3: Parse title translations
            let titleEn = finalTitle, titleIt = "", titleEs = ""
            try {
                if (titleRawJson) {
                    const tp = safeParse(titleRawJson)
                    console.log("TrustAI: Step 3 - Title parsed:", tp)
                    titleEn = tp.en || finalTitle
                    titleIt = tp.it || ""
                    titleEs = tp.es || ""
                    console.log("TrustAI: Step 3 - titleEn:", titleEn, "titleIt:", titleIt, "titleEs:", titleEs)
                }
            } catch (e) {
                console.error("TrustAI: Step 3 - Failed to parse title JSON:", e)
            }

            // Step 4: Parse description translations
            let descEn = finalContent, descIt = "", descEs = ""
            try {
                if (descRawJson) {
                    const dp = safeParse(descRawJson)
                    console.log("TrustAI: Step 4 - Desc parsed:", dp)
                    descEn = dp.en || finalContent
                    descIt = dp.it || ""
                    descEs = dp.es || ""
                    console.log("TrustAI: Step 4 - descEn length:", descEn.length, "descIt length:", descIt.length, "descEs length:", descEs.length)
                }
            } catch (e) {
                console.error("TrustAI: Step 4 - Failed to parse desc JSON:", e)
            }

            // Step 5: Set current locale fallback
            if (locale === 'en') { titleEn = titleEn || finalTitle; descEn = descEn || finalContent }
            if (locale === 'it') { titleIt = titleIt || finalTitle; descIt = descIt || finalContent }
            if (locale === 'es') { titleEs = titleEs || finalTitle; descEs = descEs || finalContent }

            const finalTitleObj = { en: titleEn, it: titleIt, es: titleEs }
            const finalDescObj = { en: descEn, it: descIt, es: descEs }

            console.log("TrustAI: Step 5 - Final title obj:", JSON.stringify(finalTitleObj))
            console.log("TrustAI: Step 5 - Final desc obj:", JSON.stringify(finalDescObj))

            onInsert({
                title: finalTitleObj,
                description: finalDescObj
            })
            handleClose()
        } catch (e) {
            console.error("TrustAI Translation Execution Failed", e)
            // Fallback: insert only current locale
            onInsert({
                title: { [locale]: finalTitle },
                description: { [locale]: finalContent }
            })
            handleClose()
        }
    }

    const handleClose = () => {
        // Reset state
        setStep('studio')
        setGeneratedTitle("")
        setGeneratedContent("")
        setError(null)

        // Clear local storage cache
        localStorage.removeItem("trustai_wizard_data")
        localStorage.removeItem("trustai_wizard_step")

        onClose()
    }

    return (
        <CustomModal
            isModalOpen={isOpen}
            handleModalClose={handleClose}
            header={""}
            style={{ width: 'auto', maxWidth: '90vw', padding: '1.5rem' }}
            isCloseIcon={false}
            isCloseXButton={false}
            isWithoutHeader={true}
            shouldCloseOnOverlayClick={false}
        >
            {step === 'studio' && (
                <TrustAIStudio
                    onGenerate={handleGenerate}
                    onClose={handleClose}
                />
            )}

            {step === 'generating' && (
                <TrustAILoading variant="generate" />
            )}

            {step === 'translating' && (
                <TrustAILoading variant="translate" />
            )}

            {step === 'error' && (
                <ErrorStepContainer>
                    <HeaderSection>
                        <TitleRow>
                            <Branding>
                                <LogoSphere />
                                <Title>{formatMessage({ id: "trustai::studio::title", defaultMessage: "TrustAI Studio" })}</Title>
                            </Branding>
                        </TitleRow>
                    </HeaderSection>
                    <ErrorContent>
                        <ErrorIcon>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ErrorIcon>
                        <ErrorTitle>
                            {formatMessage({ id: "trustai::error.title", defaultMessage: "AI Studio Error" })}
                        </ErrorTitle>
                        <ErrorMessage>{error}</ErrorMessage>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button variant="outlined" onClick={() => setStep('studio')}>
                                {formatMessage({ id: "trustai::error.back", defaultMessage: "Back to Wizard" })}
                            </Button>
                            <Button color="blue" onClick={handleClose}>
                                {formatMessage({ id: "trustai::error.close", defaultMessage: "Close" })}
                            </Button>
                        </div>
                    </ErrorContent>
                </ErrorStepContainer>
            )}

            {step === 'preview' && (
                <TrustAIPreview
                    title={generatedTitle}
                    content={generatedContent}
                    onApprove={handleApprove}
                    onRegenerate={handleRegenerate}
                    onCancel={() => setStep('studio')}
                    onClose={handleClose}
                />
            )}
        </CustomModal>
    )
}

export default TrustAIModal
